/* Gold & Richie — core behaviour
   Drawers, overlay, focus management, reveal, accordions, rails.
   No dependencies. Everything degrades to working HTML without JS. */

(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ---------------------------------------------------------------- utils */

  var GR = window.GR || (window.GR = {});

  GR.announce = function (message) {
    var region = document.querySelector('[data-gr-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  };

  GR.formatMoney = function (cents) {
    var format = GR.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  /* --------------------------------------------------------- scroll lock */

  var scrollY = 0;
  var lockCount = 0;

  function lockScroll() {
    if (lockCount === 0) {
      scrollY = window.scrollY;
      // Compensate for the scrollbar so the page does not shift sideways.
      var gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
      document.body.dataset.scrollLocked = 'true';
      document.body.style.top = '-' + scrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;
    delete document.body.dataset.scrollLocked;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, scrollY);
  }

  /* ------------------------------------------------------------- drawers */

  /* Ids, not element references. The cart drawer is re-rendered through the
     Section Rendering API on every cart mutation, which swaps the node out from
     under us; a stored element would go stale and stop matching the live one. */
  var openDrawers = [];

  function topDrawer() {
    var id = openDrawers[openDrawers.length - 1];
    return id ? document.getElementById(id) : null;
  }

  function hideOverlay() {
    var overlay = document.querySelector('[data-gr-overlay]');
    if (!overlay) return;
    overlay.dataset.open = 'false';
    window.setTimeout(function () {
      if (overlay.dataset.open !== 'true') overlay.setAttribute('hidden', '');
    }, 320);
  }

  function trapFocus(event) {
    var drawer = topDrawer();
    if (!drawer || event.key !== 'Tab') return;

    var items = Array.prototype.filter.call(
      drawer.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null; }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  GR.openDrawer = function (id, trigger) {
    var drawer = document.getElementById(id);
    if (!drawer || drawer.dataset.open === 'true') return;

    drawer.dataset.open = 'true';
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    drawer._returnFocus = trigger || document.activeElement;

    var overlay = document.querySelector('[data-gr-overlay]');
    if (overlay) {
      overlay.removeAttribute('hidden');
      // Force a frame so the transition runs rather than snapping.
      window.requestAnimationFrame(function () { overlay.dataset.open = 'true'; });
    }

    lockScroll();
    if (openDrawers.indexOf(id) === -1) openDrawers.push(id);

    window.setTimeout(function () {
      var target = drawer.querySelector('[data-gr-autofocus]') || drawer.querySelector(FOCUSABLE);
      if (target) target.focus();
    }, 120);

    document.dispatchEvent(new CustomEvent('gr:drawer:open', { detail: { id: id } }));
  };

  GR.closeDrawer = function (id) {
    var drawerId = id || openDrawers[openDrawers.length - 1];
    var drawer = drawerId ? document.getElementById(drawerId) : null;
    if (!drawer || drawer.dataset.open !== 'true') {
      // The drawer may have been removed from the DOM entirely. Drop the id so a
      // stale entry cannot keep the overlay pinned open.
      if (drawerId) {
        openDrawers = openDrawers.filter(function (d) { return d !== drawerId; });
        if (!openDrawers.length) hideOverlay();
      }
      return;
    }

    drawer.dataset.open = 'false';
    drawer.setAttribute('aria-hidden', 'true');

    openDrawers = openDrawers.filter(function (d) { return d !== drawerId; });

    if (!openDrawers.length) hideOverlay();

    unlockScroll();

    if (drawer._returnFocus && document.contains(drawer._returnFocus)) {
      drawer._returnFocus.focus();
    }

    // Hide from assistive tech only after the slide-out has finished.
    window.setTimeout(function () {
      if (drawer.dataset.open !== 'true') drawer.setAttribute('hidden', '');
    }, 520);

    document.dispatchEvent(new CustomEvent('gr:drawer:close', { detail: { id: drawer.id } }));
  };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-gr-drawer-open]');
    if (opener) {
      event.preventDefault();
      GR.openDrawer(opener.getAttribute('data-gr-drawer-open'), opener);
      return;
    }

    var closer = event.target.closest('[data-gr-drawer-close]');
    if (closer) {
      event.preventDefault();
      var explicit = closer.getAttribute('data-gr-drawer-close');
      GR.closeDrawer(explicit || null);
      return;
    }

    if (event.target.closest('[data-gr-overlay]')) {
      GR.closeDrawer();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openDrawers.length) {
      event.preventDefault();
      GR.closeDrawer();
      return;
    }
    trapFocus(event);
  });

  /* ------------------------------------------------------- reveal on scroll */

  var motionOK = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (motionOK && 'IntersectionObserver' in window) {
    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // Stagger children of a revealed group so a grid arrives as a set.
        var delay = parseFloat(entry.target.dataset.revealDelay || 0);
        window.setTimeout(function () {
          entry.target.dataset.revealed = 'true';
        }, delay);
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    var observeReveals = function (root) {
      // A hidden document never fires intersection callbacks, so arming here
      // would hide everything until the tab came back. Leave it visible.
      if (document.visibilityState === 'hidden') return;

      (root || document).querySelectorAll('.gr-reveal:not([data-revealed])').forEach(function (el) {
        el.setAttribute('data-reveal-armed', '');
        revealObserver.observe(el);
      });
    };

    document.addEventListener('DOMContentLoaded', function () { observeReveals(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') observeReveals();
    });
    observeReveals();
    GR.observeReveals = observeReveals;
  } else {
    document.querySelectorAll('.gr-reveal').forEach(function (el) { el.dataset.revealed = 'true'; });
    GR.observeReveals = function () {};
  }

  /* ---------------------------------------------------------- disclosures */

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-gr-accordion-toggle]');
    if (!toggle) return;

    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));

    toggle.setAttribute('aria-expanded', String(!expanded));
    if (!panel) return;

    // `hidden` keeps collapsed content out of the accessibility tree and the
    // tab order. The content fades in on open via CSS animation.
    panel.hidden = expanded;
  });

  /* ---------------------------------------------------------------- rails */

  // Arrow controls for horizontal rails; hidden when the rail does not overflow.
  function initRail(rail) {
    var prev = rail.parentElement.querySelector('[data-gr-rail-prev]');
    var next = rail.parentElement.querySelector('[data-gr-rail-next]');
    if (!prev || !next) return;

    function update() {
      var overflowing = rail.scrollWidth > rail.clientWidth + 4;
      prev.hidden = !overflowing;
      next.hidden = !overflowing;
      if (!overflowing) return;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    }

    function scrollBy(direction) {
      var first = rail.firstElementChild;
      var step = first ? first.getBoundingClientRect().width + 16 : rail.clientWidth * 0.8;
      rail.scrollBy({ left: step * direction, behavior: motionOK ? 'smooth' : 'auto' });
    }

    prev.addEventListener('click', function () { scrollBy(-1); });
    next.addEventListener('click', function () { scrollBy(1); });
    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function initRails(root) {
    (root || document).querySelectorAll('[data-gr-rail]').forEach(initRail);
  }

  document.addEventListener('DOMContentLoaded', function () { initRails(); });
  initRails();
  GR.initRails = initRails;

  /* ------------------------------------------------------ quantity steppers */

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-gr-qty]');
    if (!btn) return;

    var wrap = btn.closest('.gr-qty');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;

    var step = btn.getAttribute('data-gr-qty') === 'up' ? 1 : -1;
    var min = parseInt(input.getAttribute('min') || '0', 10);
    var max = input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;
    var next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + step));

    if (next === parseInt(input.value, 10)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------- mobile menu panels */

  // Sliding panels rather than nested accordions: this catalog is three levels
  // deep, and accordions push the first item off screen at that depth.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-gr-menu-to]');
    if (!trigger) return;

    var container = trigger.closest('[data-gr-menu-panels]');
    if (!container) return;

    var targetName = trigger.getAttribute('data-gr-menu-to');
    var target = container.querySelector('[data-gr-menu-panel="' + targetName + '"]');
    if (!target) return;

    container.querySelectorAll('[data-gr-menu-panel]').forEach(function (panel) {
      delete panel.dataset.active;
    });
    target.dataset.active = 'true';
    container.scrollTop = 0;

    var focusTarget = target.querySelector('.gr-menu-panel__back') || target.querySelector(FOCUSABLE);
    if (focusTarget) focusTarget.focus();
  });

  // Reset the menu to its root whenever it closes, so it never reopens mid-tree.
  document.addEventListener('gr:drawer:close', function (event) {
    if (event.detail.id !== 'gr-menu-drawer') return;
    var container = document.querySelector('[data-gr-menu-panels]');
    if (!container) return;
    container.querySelectorAll('[data-gr-menu-panel]').forEach(function (panel) {
      if (panel.getAttribute('data-gr-menu-panel') === 'root') {
        panel.dataset.active = 'true';
      } else {
        delete panel.dataset.active;
      }
    });
  });

  /* ------------------------------------------------------------ sticky header */

  var header = document.querySelector('[data-gr-header]');

  // Publish the header's real height so the hero can slide up beneath it.
  if (header) {
    var measure = function () {
      document.documentElement.style.setProperty('--gr-header-h', header.offsetHeight + 'px');
    };
    measure();
    window.addEventListener('resize', measure);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
  }

  if (header && header.dataset.sticky === 'true') {
    var lastY = window.scrollY;

    var onScroll = function () {
      var y = window.scrollY;

      // Solid once past the hero edge; transparent only at the very top.
      header.dataset.scrolled = y > 40 ? 'true' : 'false';

      // Hide on the way down, reveal on the way up — but never over the hero.
      if (y > 240 && y > lastY) {
        header.dataset.hidden = 'true';
      } else {
        header.dataset.hidden = 'false';
      }
      lastY = y;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------ theme editor re-binding */

  document.addEventListener('shopify:section:load', function (event) {
    GR.observeReveals(event.target);
    initRails(event.target);
  });
})();
